/**
 * Enter adds the next row under one: on Home, in a tab, on a page. The new row takes what keeps
 * it in its card: what every row you see in the card has in common (two or more of them: a detail,
 * who it's for, the day, a choice or date detail) and, in a tab, what the tab groups or filters by,
 * from the row it came from (so it stays in the tab, in the same group). Never times, reminders,
 * repeats, amounts or notes: those belong to the one thing.
 */
import { hasValue, type ItemInit, type ItemSnapshot } from '@terrazzo/core'
import { store } from '../store/store'
import { viewOf } from './charts'

type Init = Omit<ItemInit, 'title'>
/** Where a row is shown: the tab, and the group it's in there (a detail's value, a person, a day). */
export interface RowPlace {
  view: string
  group?: string
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
const CATEGORY = new Set(['select', 'date'])

/** What every one of these rows has (two or more of them). */
function shared(ids: string[]): Init {
  const items = ids.map((id) => store.get(id)).filter((x): x is ItemSnapshot => !!x && !store.tree.inTrash.has(x.id))
  if (items.length < 2) return {}
  const [first, ...rest] = items as [ItemSnapshot, ...ItemSnapshot[]]
  const tags = first.tags.filter((t) => rest.every((it) => it.tags.includes(t)))
  const assignee = first.assignee && rest.every((it) => it.assignee === first.assignee) ? first.assignee : undefined
  const date = first.date && rest.every((it) => it.date === first.date) ? first.date : undefined
  const fields = store.fields()
  const props: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(first.props)) if (CATEGORY.has(fields.get(k)?.type ?? '') && hasValue(v) && rest.every((it) => same(it.props[k], v))) props[k] = v
  return { ...(tags.length ? { tags } : {}), ...(assignee ? { assignee } : {}), ...(date ? { date } : {}), ...(Object.keys(props).length ? { props } : {}) }
}

/** What the tab needs from the row so the new one stays in it, in the same group. */
function needs(it: ItemSnapshot, place: RowPlace): Init {
  const view = viewOf(place.view)
  if (!view) return {}
  const f = view.filter
  const g = view.groupBy
  const tags = new Set<string>()
  const under = (p: string) => it.tags.filter((t) => t === p || t.startsWith(`${p}/`))
  for (const p of f.tags ?? []) under(p).forEach((t) => tags.add(t))
  if (g === 'tag' || g?.startsWith('tag:')) under(place.group ?? (g === 'tag' ? '' : g.slice(4))).forEach((t) => tags.add(t))
  const fields = store.fields()
  const props: Record<string, unknown> = {}
  for (const k of [g?.startsWith('field:') ? g.slice(6) : null, f.hasField ?? null]) {
    const type = k ? fields.get(k)?.type : undefined
    if (k && hasValue(it.props[k]) && type !== 'money' && type !== 'number') props[k] = it.props[k]
  }
  const byPerson = g === 'assignee' || (!!f.assignee && f.assignee !== 'none')
  const byDay = g === 'date' || !!f.from || !!f.to || !!f.withinDays
  return {
    ...(tags.size ? { tags: [...tags] } : {}),
    ...(byPerson && it.assignee ? { assignee: it.assignee } : {}),
    ...(byDay && it.date ? { date: it.date } : {}),
    ...(Object.keys(props).length ? { props } : {}),
  }
}

/** A new row's start, from the row it came from (`from`), the rows you see in its card (`siblings`) and where they're shown. */
export function newRowFrom(from: string, siblings: string[], place?: RowPlace | null): Init {
  const it = store.get(from)
  const a = shared(siblings)
  const b = it && place ? needs(it, place) : {}
  const tags = [...new Set([...(a.tags ?? []), ...(b.tags ?? [])])]
  const props = { ...a.props, ...b.props }
  return { ...a, ...b, ...(tags.length ? { tags } : {}), ...(Object.keys(props).length ? { props } : {}) }
}
